import { Link } from 'react-router-dom';

import PremiumGate from '@/components/premium/PremiumGate.tsx';
import { ROUTES } from '@/config/routes.ts';
import { clubSidebar } from '@/config/sidebarConfig.ts';
import { getClubAnalyticsApi, getClubApi } from '@/lib/api/clubsApi';
import { useAuthStore } from '@/stores/auth-store';
import { COMPETITION_TYPE_LABELS as TYPE_LABELS, isClubProActive } from '@shared';
import { CompetitionType } from '@shared/enums/domain.enum';
import type { ClubAnalyticsRecentMember, ClubAnalyticsTopPlayer } from '@shared/types/entities';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Edit, Info, MapPin, Medal, Phone, Star, TrendingUp, Trophy, Users, Zap } from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';

// ─── Helpers ──────────────────────────────────────────────────────

const RANK_CONFIG: Record<number, { emoji: string; bg: string; border: string; avatarBg: string }> = {
  1: {
    emoji: '🥇',
    bg: 'bg-gradient-to-r from-yellow-50 to-amber-50',
    border: 'border-2 border-yellow-200',
    avatarBg: 'bg-gradient-to-br from-yellow-400 to-yellow-600',
  },
  2: {
    emoji: '🥈',
    bg: 'bg-slate-50',
    border: 'border border-slate-200',
    avatarBg: 'bg-gradient-to-br from-slate-300 to-slate-500',
  },
  3: {
    emoji: '🥉',
    bg: 'bg-orange-50',
    border: 'border border-orange-200',
    avatarBg: 'bg-gradient-to-br from-orange-400 to-orange-600',
  },
};

const MEMBER_GRADIENTS = [
  'from-blue-500 to-purple-600',
  'from-emerald-500 to-teal-600',
  'from-orange-500 to-red-600',
  'from-purple-500 to-indigo-600',
  'from-cyan-500 to-blue-600',
];

const TOP_RANK_CONFIG = [
  'bg-gradient-to-br from-yellow-400 to-yellow-600',
  'bg-gradient-to-br from-slate-300 to-slate-500',
  'bg-gradient-to-br from-orange-400 to-orange-600',
  'bg-slate-200',
  'bg-slate-200',
];

function getInitials(firstName: string | null, lastName: string | null, username: string) {
  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
  if (firstName) return firstName.slice(0, 2).toUpperCase();
  return username.slice(0, 2).toUpperCase();
}

function getDisplayName(p: ClubAnalyticsRecentMember | ClubAnalyticsTopPlayer) {
  if (p.firstName && p.lastName) return `${p.firstName} ${p.lastName}`;
  return p.username;
}

function formatDaysUntil(days: number) {
  if (days === 0) return "Aujourd'hui";
  if (days === 1) return 'Demain';
  return `Dans ${days} jour${days > 1 ? 's' : ''}`;
}

function progressWidth(value: number, total: number, fallback = 60) {
  if (total === 0) return `${fallback}%`;
  return `${Math.min(100, Math.round((value / total) * 100))}%`;
}

// ─── Skeleton helpers ─────────────────────────────────────────────

function SkeletonCard() {
  return <div className='bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl p-6 animate-pulse h-32' />;
}

function SkeletonRow() {
  return (
    <div className='flex items-center gap-4 p-4 animate-pulse'>
      <div className='w-12 h-12 bg-slate-200 rounded-full flex-shrink-0' />
      <div className='flex-1 space-y-2'>
        <div className='h-4 bg-slate-200 rounded w-40' />
        <div className='h-3 bg-slate-200 rounded w-24' />
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────

export default function ClubAnalytics() {
  const clubId = useAuthStore(s => s.user)?.clubId;
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const user = useAuthStore(s => s.user);
  const isProActive = isClubProActive(user?.club?.clubPlan, user?.club?.subscriptionStatus);

  const {
    data: club,
    isLoading: clubLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['clubs', clubId],
    queryFn: () => getClubApi(clubId!),
    enabled: !!clubId && isAuthenticated,
    staleTime: 10 * 60 * 1000,
  });

  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['clubs', clubId, 'analytics'],
    queryFn: () => getClubAnalyticsApi(clubId!),
    enabled: !!clubId && isAuthenticated && isProActive,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = clubLoading || analyticsLoading;

  if (isError) {
    return (
      <DashboardLayout sidebarConfig={clubSidebar}>
        <div className='p-8'>
          <QueryErrorState onRetry={refetch} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebarConfig={clubSidebar}>
      <PremiumGate
        isAllowed={isProActive}
        title='Mes statistiques (Pro)'
        description='Abonnez-vous pour débloquer l’analyse complète de vos performances.'
        ctaLabel='Voir l’abonnement'
        ctaTo='/club/settings?tab=subscription'
      >
        <div className='p-8'>
          <PageHeader
            title={club?.name ?? 'Mon club'}
            subtitle='Gérez votre club et vos membres'
            actions={
              <Link
                to={ROUTES.CLUB_PROFILE_EDIT}
                className='px-6 py-2.5 border border-slate-200 hover:bg-slate-50 rounded-lg font-semibold transition flex items-center gap-2'
              >
                <Edit className='w-5 h-5' />
                Modifier le profil
              </Link>
            }
          />

          {/* Quick stats — gradient cards */}
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
            {isLoading ? (
              [0, 1, 2, 3].map(i => <SkeletonCard key={i} />)
            ) : (
              <>
                {/* Membres */}
                <div className='bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6'>
                  <div className='flex items-center justify-between mb-3'>
                    <div className='w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center'>
                      <Users className='w-6 h-6 text-white' />
                    </div>
                    {(analytics?.overview.newMembersThisMonth ?? 0) > 0 && (
                      <span className='text-xs text-blue-700 font-semibold'>
                        +{analytics?.overview.newMembersThisMonth} ce mois
                      </span>
                    )}
                  </div>
                  <p className='text-3xl font-bold text-slate-900'>{analytics?.overview.totalMembers ?? '—'}</p>
                  <p className='text-sm text-slate-600'>Membres</p>
                </div>

                {/* Victoires cette année */}
                <div className='bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-6'>
                  <div className='flex items-center justify-between mb-3'>
                    <div className='w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center'>
                      <Trophy className='w-6 h-6 text-white' />
                    </div>
                    {(analytics?.overview.newWinsThisMonth ?? 0) > 0 && (
                      <span className='text-xs text-emerald-700 font-semibold'>
                        +{analytics?.overview.newWinsThisMonth} ce mois
                      </span>
                    )}
                  </div>
                  <p className='text-3xl font-bold text-slate-900'>{analytics?.overview.winsThisYear ?? '—'}</p>
                  <p className='text-sm text-slate-600'>Victoires cette année</p>
                </div>

                {/* Concours à venir */}
                <div className='bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6'>
                  <div className='flex items-center justify-between mb-3'>
                    <div className='w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center'>
                      <Calendar className='w-6 h-6 text-white' />
                    </div>
                  </div>
                  <p className='text-3xl font-bold text-slate-900'>{analytics?.overview.upcomingCompetitions ?? '—'}</p>
                  <p className='text-sm text-slate-600'>Concours à venir</p>
                </div>

                {/* Podiums cette année */}
                <div className='bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6'>
                  <div className='flex items-center justify-between mb-3'>
                    <div className='w-12 h-12 bg-amber-600 rounded-lg flex items-center justify-center'>
                      <Medal className='w-6 h-6 text-white' />
                    </div>
                  </div>
                  <p className='text-3xl font-bold text-slate-900'>{analytics?.overview.podiumsThisYear ?? '—'}</p>
                  <p className='text-sm text-slate-600'>Podiums cette année</p>
                </div>
              </>
            )}
          </div>

          {/* Main content grid */}
          <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
            {/* Colonne principale (2/3) */}
            <div className='lg:col-span-2 space-y-8'>
              {/* Informations du club */}
              <div className='bg-white rounded-xl border border-slate-200 p-8'>
                <div className='flex items-center justify-between mb-6'>
                  <h2 className='text-xl font-bold text-slate-900 flex items-center gap-3'>
                    <div className='w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center'>
                      <Info className='w-5 h-5 text-blue-600' />
                    </div>
                    Informations du club
                  </h2>
                  <Link
                    to={ROUTES.CLUB_PROFILE_EDIT}
                    className='px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg font-semibold transition flex items-center gap-2'
                  >
                    <Edit className='w-4 h-4' />
                    Modifier
                  </Link>
                </div>

                {clubLoading ? (
                  <div className='space-y-4 animate-pulse'>
                    <div className='grid grid-cols-2 gap-6'>
                      <div className='h-12 bg-slate-100 rounded' />
                      <div className='h-12 bg-slate-100 rounded' />
                    </div>
                    <div className='h-16 bg-slate-100 rounded' />
                    <div className='grid grid-cols-2 gap-6'>
                      <div className='h-12 bg-slate-100 rounded' />
                      <div className='h-12 bg-slate-100 rounded' />
                    </div>
                  </div>
                ) : (
                  <div className='space-y-6'>
                    <div className='grid grid-cols-2 gap-6'>
                      <div>
                        <label className='block text-sm font-semibold text-slate-600 mb-2'>Nom du club</label>
                        <p className='text-slate-900 font-semibold'>{club?.name ?? '—'}</p>
                      </div>
                      {club?.registrationNumber && (
                        <div>
                          <label className='block text-sm font-semibold text-slate-600 mb-2'>
                            Numéro d'affiliation
                          </label>
                          <p className='text-slate-900 font-semibold font-mono'>{club.registrationNumber}</p>
                        </div>
                      )}
                    </div>

                    {club?.description && (
                      <div>
                        <label className='block text-sm font-semibold text-slate-600 mb-2'>Description</label>
                        <p className='text-slate-700 leading-relaxed'>{club.description}</p>
                      </div>
                    )}

                    <div className='grid grid-cols-2 gap-6'>
                      {(club?.address || club?.city) && (
                        <div>
                          <label className='block text-sm font-semibold text-slate-600 mb-2 flex items-center gap-1'>
                            <MapPin className='w-3.5 h-3.5' />
                            Adresse
                          </label>
                          {club?.address && <p className='text-slate-700'>{club.address}</p>}
                          {club?.city && (
                            <p className='text-slate-700'>
                              {club.postcode} {club.city}
                            </p>
                          )}
                        </div>
                      )}
                      {(club?.email || club?.phone) && (
                        <div>
                          <label className='block text-sm font-semibold text-slate-600 mb-2 flex items-center gap-1'>
                            <Phone className='w-3.5 h-3.5' />
                            Contact
                          </label>
                          {club?.email && <p className='text-slate-700'>{club.email}</p>}
                          {club?.phone && <p className='text-slate-700'>{club.phone}</p>}
                        </div>
                      )}
                    </div>

                    {!club?.description && !club?.address && !club?.email && (
                      <div className='flex items-center gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100'>
                        <Info className='w-5 h-5 text-blue-500 flex-shrink-0' />
                        <p className='text-sm text-blue-700'>
                          Complétez le profil de votre club pour qu'il apparaisse dans l'annuaire public.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Membres récents */}
              <div className='bg-white rounded-xl border border-slate-200 p-8'>
                <div className='flex items-center justify-between mb-6'>
                  <h2 className='text-xl font-bold text-slate-900 flex items-center gap-3'>
                    <div className='w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center'>
                      <Users className='w-5 h-5 text-emerald-600' />
                    </div>
                    Membres récents
                  </h2>
                </div>

                <div className='space-y-3'>
                  {isLoading ? (
                    [0, 1, 2, 3, 4].map(i => <SkeletonRow key={i} />)
                  ) : !analytics?.recentMembers.length ? (
                    <p className='text-slate-500 text-sm py-4 text-center'>Aucun membre pour l'instant</p>
                  ) : (
                    analytics.recentMembers.map((m, i) => (
                      <div
                        key={m.id}
                        className='flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition'
                      >
                        <div className='flex items-center gap-4'>
                          <div
                            className={`w-12 h-12 bg-gradient-to-br ${MEMBER_GRADIENTS[i % MEMBER_GRADIENTS.length]} rounded-full flex items-center justify-center text-white font-bold`}
                          >
                            {getInitials(m.firstName, m.lastName, m.username)}
                          </div>
                          <div>
                            <p className='font-semibold text-slate-900'>{getDisplayName(m)}</p>
                            <div className='flex items-center gap-3 text-xs text-slate-500'>
                              <span>Inscrit en {new Date(m.createdAt).getFullYear()}</span>
                              {m.winsTotal > 0 && (
                                <>
                                  <span>·</span>
                                  <span className='flex items-center gap-1'>
                                    <Trophy className='w-3 h-3' />
                                    {m.winsTotal} victoire{m.winsTotal > 1 ? 's' : ''}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <span className='px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold'>
                          Actif
                        </span>
                      </div>
                    ))
                  )}
                </div>

                <Link
                  to={ROUTES.CLUB_MEMBERS}
                  className='w-full mt-4 px-4 py-3 border border-slate-200 hover:bg-slate-50 rounded-lg font-semibold transition block text-center text-sm'
                >
                  Voir tous les membres
                  {analytics?.overview.totalMembers ? ` (${analytics.overview.totalMembers})` : ''}
                </Link>
              </div>

              {/* Derniers podiums */}
              <div className='bg-white rounded-xl border border-slate-200 p-8'>
                <div className='flex items-center justify-between mb-6'>
                  <h2 className='text-xl font-bold text-slate-900 flex items-center gap-3'>
                    <div className='w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center'>
                      <Trophy className='w-5 h-5 text-yellow-600' />
                    </div>
                    Derniers podiums
                  </h2>
                  <Link
                    to={ROUTES.CLUB_COMPETITIONS}
                    className='text-blue-600 hover:text-blue-700 font-semibold text-sm'
                  >
                    Voir tout →
                  </Link>
                </div>

                {isLoading ? (
                  <div className='space-y-4 animate-pulse'>
                    {[0, 1, 2].map(i => (
                      <div key={i} className='h-20 bg-slate-100 rounded-xl' />
                    ))}
                  </div>
                ) : !analytics?.recentPodiums.length ? (
                  <div className='py-8 text-center text-slate-500 text-sm'>
                    <Trophy className='w-10 h-10 mx-auto mb-3 text-slate-300' />
                    Pas encore de podium — continuez à vous inscrire aux concours !
                  </div>
                ) : (
                  <div className='space-y-4'>
                    {analytics.recentPodiums.map(podium => {
                      const cfg = RANK_CONFIG[podium.rank] ?? RANK_CONFIG[3];
                      return (
                        <div
                          key={podium.teamId}
                          className={`flex items-start gap-4 p-4 ${cfg.bg} rounded-xl ${cfg.border}`}
                        >
                          <div
                            className={`w-12 h-12 ${cfg.avatarBg} rounded-xl flex items-center justify-center text-2xl shadow-lg flex-shrink-0`}
                          >
                            {cfg.emoji}
                          </div>
                          <div className='flex-1 min-w-0'>
                            <p className='font-bold text-slate-900 mb-0.5 truncate'>{podium.competitionName}</p>
                            <p className='text-sm text-slate-600 mb-1'>{podium.teamName}</p>
                            {podium.captainName && (
                              <p className='text-xs text-slate-500 mb-2'>Capitaine : {podium.captainName}</p>
                            )}
                            <div className='flex items-center gap-3 text-xs text-slate-500'>
                              <span className='flex items-center gap-1'>
                                <Calendar className='w-3 h-3' />
                                {new Date(podium.competitionDate).toLocaleDateString('fr-FR', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                })}
                              </span>
                              {(podium.venue || podium.city) && (
                                <span className='flex items-center gap-1'>
                                  <MapPin className='w-3 h-3' />
                                  {podium.venue ?? podium.city}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar (1/3) */}
            <div className='col-span-1 space-y-8'>
              {/* Concours à venir */}
              <div className='bg-white rounded-xl border border-slate-200 p-6'>
                <h3 className='font-bold text-slate-900 mb-4 flex items-center gap-2'>
                  <Calendar className='w-5 h-5 text-blue-600' />
                  Concours à venir
                </h3>

                {isLoading ? (
                  <div className='space-y-3 animate-pulse'>
                    {[0, 1, 2].map(i => (
                      <div key={i} className='h-20 bg-slate-100 rounded-xl' />
                    ))}
                  </div>
                ) : !analytics?.upcomingTeams.length ? (
                  <p className='text-slate-500 text-sm py-2'>Aucune inscription à venir</p>
                ) : (
                  <div className='space-y-3'>
                    {analytics.upcomingTeams.map((t, i) => (
                      <div
                        key={t.teamId}
                        className={`p-4 rounded-xl border ${i === 0 ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}
                      >
                        <div className='flex items-center gap-2 mb-2'>
                          <div
                            className={`w-8 h-8 ${i === 0 ? 'bg-blue-600' : 'bg-slate-600'} rounded-lg flex items-center justify-center`}
                          >
                            <Trophy className='w-4 h-4 text-white' />
                          </div>
                          <span className={`text-xs font-semibold ${i === 0 ? 'text-blue-700' : 'text-slate-600'}`}>
                            {formatDaysUntil(t.daysUntil)}
                          </span>
                        </div>
                        <p className='font-bold text-slate-900 text-sm mb-1 truncate'>{t.competitionName}</p>
                        <p className='text-xs text-slate-600 mb-1'>
                          {t.competitionCity ?? '—'} ·{' '}
                          {TYPE_LABELS[t.competitionType as CompetitionType] ?? t.competitionType}
                        </p>
                        <p className='text-xs text-slate-500'>
                          {t.memberCount} membre{t.memberCount > 1 ? 's' : ''} inscrit
                          {t.memberCount > 1 ? 's' : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                <Link
                  to={ROUTES.CLUB_COMPETITIONS}
                  className='w-full mt-4 px-4 py-2 border border-slate-200 hover:bg-slate-50 rounded-lg font-semibold text-sm transition block text-center'
                >
                  Voir toutes les inscriptions
                </Link>
              </div>

              {/* Top joueurs cette année */}
              <div className='bg-white rounded-xl border border-slate-200 p-6'>
                <h3 className='font-bold text-slate-900 mb-4 flex items-center gap-2'>
                  <Star className='w-5 h-5 text-amber-600' />
                  Top joueurs cette année
                </h3>

                {isLoading ? (
                  <div className='space-y-3 animate-pulse'>
                    {[0, 1, 2].map(i => (
                      <div key={i} className='h-12 bg-slate-100 rounded-xl' />
                    ))}
                  </div>
                ) : !analytics?.topPlayers.length ? (
                  <p className='text-slate-500 text-sm py-2'>Pas encore de victoires cette année</p>
                ) : (
                  <div className='space-y-3'>
                    {analytics.topPlayers.map((p, i) => (
                      <div key={p.userId} className='flex items-center gap-3'>
                        <div
                          className={`w-8 h-8 ${TOP_RANK_CONFIG[i] ?? TOP_RANK_CONFIG[4]} rounded-full flex items-center justify-center text-white font-bold text-sm`}
                        >
                          {i + 1}
                        </div>
                        <div
                          className={`w-10 h-10 bg-gradient-to-br ${MEMBER_GRADIENTS[i % MEMBER_GRADIENTS.length]} rounded-full flex items-center justify-center text-white font-bold text-sm`}
                        >
                          {getInitials(p.firstName, p.lastName, p.username)}
                        </div>
                        <div className='flex-1 min-w-0'>
                          <p className='font-semibold text-slate-900 text-sm truncate'>{getDisplayName(p)}</p>
                          <p className='text-xs text-slate-500'>
                            {p.winsThisYear} victoire{p.winsThisYear > 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions rapides */}
              <div className='bg-white rounded-xl border border-slate-200 p-6'>
                <h3 className='font-bold text-slate-900 mb-4 flex items-center gap-2'>
                  <Zap className='w-5 h-5 text-purple-600' />
                  Actions rapides
                </h3>

                <div className='space-y-2'>
                  <Link
                    to={ROUTES.CLUB_MEMBERS}
                    className='w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition flex items-center justify-center gap-2'
                  >
                    <Users className='w-5 h-5' />
                    Gérer les membres
                  </Link>
                  <Link
                    to={ROUTES.CLUB_COMPETITIONS}
                    className='w-full px-4 py-3 border border-slate-200 hover:bg-slate-50 rounded-lg font-semibold transition flex items-center justify-center gap-2'
                  >
                    <Trophy className='w-5 h-5' />
                    Inscriptions aux concours
                  </Link>
                  <Link
                    to={ROUTES.CLUB_PROFILE_EDIT}
                    className='w-full px-4 py-3 border border-slate-200 hover:bg-slate-50 rounded-lg font-semibold transition flex items-center justify-center gap-2'
                  >
                    <Edit className='w-5 h-5' />
                    Modifier le profil
                  </Link>
                </div>
              </div>

              {/* Progression ce mois */}
              <div className='bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl p-6 text-white'>
                <h3 className='font-bold mb-4 flex items-center gap-2'>
                  <TrendingUp className='w-5 h-5' />
                  Ce mois-ci
                </h3>

                {isLoading ? (
                  <div className='space-y-4 animate-pulse'>
                    {[0, 1, 2].map(i => (
                      <div key={i} className='h-8 bg-white/20 rounded' />
                    ))}
                  </div>
                ) : (
                  <div className='space-y-4'>
                    <div>
                      <div className='flex items-center justify-between mb-2'>
                        <span className='text-sm text-blue-100'>Nouveaux membres</span>
                        <span className='font-bold'>
                          {analytics?.thisMonth.newMembers ? `+${analytics.thisMonth.newMembers}` : '0'}
                        </span>
                      </div>
                      <div className='h-2 bg-white/20 rounded-full overflow-hidden'>
                        <div
                          className='h-full bg-white rounded-full transition-all'
                          style={{
                            width: progressWidth(
                              analytics?.thisMonth.newMembers ?? 0,
                              analytics?.overview.totalMembers ?? 1,
                              analytics?.thisMonth.newMembers ? 60 : 5,
                            ),
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <div className='flex items-center justify-between mb-2'>
                        <span className='text-sm text-blue-100'>Victoires</span>
                        <span className='font-bold'>
                          {analytics?.thisMonth.wins ? `+${analytics.thisMonth.wins}` : '0'}
                        </span>
                      </div>
                      <div className='h-2 bg-white/20 rounded-full overflow-hidden'>
                        <div
                          className='h-full bg-white rounded-full transition-all'
                          style={{
                            width: progressWidth(
                              analytics?.thisMonth.wins ?? 0,
                              analytics?.overview.winsThisYear ?? 1,
                              analytics?.thisMonth.wins ? 40 : 5,
                            ),
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <div className='flex items-center justify-between mb-2'>
                        <span className='text-sm text-blue-100'>Concours participés</span>
                        <span className='font-bold'>
                          {analytics?.thisMonth.teamsPlayed ? `+${analytics.thisMonth.teamsPlayed}` : '0'}
                        </span>
                      </div>
                      <div className='h-2 bg-white/20 rounded-full overflow-hidden'>
                        <div
                          className='h-full bg-white rounded-full transition-all'
                          style={{
                            width: progressWidth(
                              analytics?.thisMonth.teamsPlayed ?? 0,
                              analytics?.overview.teamsThisYear ?? 1,
                              analytics?.thisMonth.teamsPlayed ? 80 : 5,
                            ),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </PremiumGate>
    </DashboardLayout>
  );
}
